// The confirm sheet behind the cover-page upsells (the loader's "While you
// wait" card, the ready cover's aside and its Keep going rows). Two offers to
// someone who already pays for Basic:
//
// - creator: Basic -> Creator. Shows what it costs TODAY, a proration Stripe
//   computes (upgrade-preview, same coupon as the charge path), and the
//   monthly price after. Same charge as CreatorUpsellSheet; this is its dark
//   twin for the cover page, where the pitch is the cover in front of them.
// - yearly: Basic monthly -> Basic yearly, the PR #495 post-purchase offer.
//   It leads with the yearly price per month against what they pay monthly
//   now, and states the amount charged today right under the button (a
//   per-month headline with a ~12x charge behind it is the shape of a "didn't
//   authorise this" dispute). Banks that want 3D Secure get it with the card
//   on file; until it clears nothing is charged.
//
// No guessed prices: when the preview fails there is no button to press.
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Check } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js/pure";
import { toast } from "@jammable/ui-core";
import { JamSheet } from "@jammable/ui-components/Dialog";
import { Spinner } from "@jammable/ui-components/Loader";
import { getAuthHeaders } from "@jammable/clientapi/src/shared";
import { stripeAmountToMajor } from "@jammable/clientapi/src/payments";
import { mutationSubscriptionUpgrade } from "../../../../packages/ui/api/src/payments/mutation";
import { useAuth } from "../../context/AuthProvider";
import { formatPriceWithLocale } from "../../utils/pricing";
import { track } from "../../utils/analytics";

export type CoverUpsellOffer = "creator" | "yearly";

export type UpgradePreview = {
  currency: string;
  dueToday: number;
  regularAmount: number;
  currentMonthlyAmount: number | null;
};

const PREVIEW_URL: Record<CoverUpsellOffer, string> = {
  creator: "/api/payment/subscription/upgrade-preview?plan=Jammable%20Creator",
  yearly:
    "/api/payment/subscription/upgrade-preview?plan=Jammable%20Basic&interval=year",
};

// One Stripe preview per offer per visit, shared by every card and sheet on
// the page. A 409/404 (not on monthly Basic, no annual price in their
// currency) is an error here, which hides the yearly offer.
export const useUpgradePreview = (offer: CoverUpsellOffer, enabled: boolean) =>
  useQuery<UpgradePreview>(
    ["upgrade-preview", offer],
    async () => {
      const res = await fetch(PREVIEW_URL[offer], {
        headers: { ...(await getAuthHeaders()) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    { enabled, staleTime: 10 * 60 * 1000, retry: false },
  );

/** The yearly offer's figures, in major units, formatted. */
export const yearlyFigures = (preview: UpgradePreview) => {
  const cur = preview.currency || "usd";
  const money = (n: number) => formatPriceWithLocale(n, cur);
  const today = Math.max(0, stripeAmountToMajor(preview.dueToday, cur));
  const yearly = stripeAmountToMajor(preview.regularAmount, cur);
  const monthly = stripeAmountToMajor(preview.currentMonthlyAmount ?? 0, cur);
  const perMonth = yearly / 12;
  const pct =
    monthly > perMonth ? Math.round((1 - perMonth / monthly) * 100) : 0;
  return {
    today: money(today),
    chargedToday: today > 0,
    yearly: money(yearly),
    monthly: monthly > perMonth ? money(monthly) : null,
    perMonth: money(perMonth),
    pct,
  };
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const Perks = ({ items }: { items: string[] }) => (
  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
    {items.map((line) => (
      <li
        key={line}
        className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-bold"
      >
        <span className="tw-flex tw-h-[22px] tw-w-[22px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
          <Check size={12} strokeWidth={3.5} />
        </span>
        {line}
      </li>
    ))}
  </ul>
);

export default function CoverUpsellSheet({
  open,
  offer,
  source,
  title,
  coverMaking,
  onClose,
  onUpgraded,
}: {
  open: boolean;
  offer: CoverUpsellOffer;
  /** Saved on the Stripe subscription as upgrade_source, e.g. "cover-loader:train". */
  source: string;
  /** The Creator sheet's headline, after what the card offered. */
  title?: string;
  /** Opened from the loader: say the cover keeps going. */
  coverMaking?: boolean;
  onClose: () => void;
  /** Upgraded, and the account refreshed. */
  onUpgraded?: () => void;
}) {
  const { updateUser } = useAuth();
  const queryClient = useQueryClient();
  const { data: preview, isError } = useUpgradePreview(offer, open);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The upgrade API records the new plan on the account straight away; the
  // retries cover a slow write. updateUser re-renders the page on the new
  // plan, which is what takes the upsell cards away.
  const refreshAccount = async () => {
    for (let i = 0; i < 4; i++) {
      try {
        const res = await fetch("/api/user", {
          headers: { ...(await getAuthHeaders()) },
        });
        if (res.ok) {
          updateUser(await res.json());
          return;
        }
      } catch {
        // Try again.
      }
      await sleep(1000);
    }
  };

  const upgrade = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    track("Cover Upsell Accepted", {
      offer,
      source,
      dueToday: preview.dueToday,
      currency: preview.currency,
    });
    try {
      const result = await mutationSubscriptionUpgrade(
        offer === "yearly" ? "Jammable Basic" : "Jammable Creator",
        offer === "yearly" ? "year" : "month",
        undefined,
        source,
      );
      // Yearly only: the switch is pending_if_incomplete, so backing out of
      // the bank's check leaves them on monthly and charges nothing.
      if (result?.requiresAction && result.clientSecret) {
        track("Cover Upsell 3DS", { offer, source });
        const stripe = await loadStripe(
          process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string,
        );
        const confirmed = await stripe?.confirmCardPayment(result.clientSecret);
        if (!stripe || confirmed?.error) {
          setError(
            "Your bank didn't confirm the payment, so nothing was charged. You can try again.",
          );
          setBusy(false);
          return;
        }
      }
    } catch (e) {
      console.warn("cover upsell: upgrade failed", e);
      // Creator is error_if_incomplete: a declined payment changes nothing.
      setError(
        "That didn't go through, so nothing was charged. You can try again or change plans in Settings.",
      );
      setBusy(false);
      return;
    }
    track("Cover Upsell Upgraded", { offer, source });
    await refreshAccount();
    // Neither preview applies any more (yearly now answers 409).
    queryClient.removeQueries(["upgrade-preview"]);
    setBusy(false);
    toast.success(
      offer === "yearly"
        ? "You're on yearly Basic. Twelve months, locked in!"
        : "You're on Pro. Time to make your own voice!",
    );
    onUpgraded?.();
  };

  const close = () => {
    if (busy) return;
    track("Cover Upsell Sheet Closed", { offer, source });
    onClose();
  };

  const y = offer === "yearly" && preview ? yearlyFigures(preview) : null;
  const cur = preview?.currency || "usd";
  const money = (amount: number) =>
    formatPriceWithLocale(stripeAmountToMajor(amount, cur), cur);
  const creatorToday = preview ? Math.max(0, preview.dueToday) : 0;

  const cta = !preview
    ? "Upgrade"
    : busy
      ? offer === "yearly"
        ? "Switching…"
        : "Upgrading…"
      : y
        ? `Switch me to ${y.perMonth}/month`
        : creatorToday > 0
          ? `Upgrade and pay ${money(creatorToday)}`
          : "Upgrade, nothing to pay today";

  const fine = y
    ? `${y.chargedToday ? `${y.today} charged today` : "Nothing charged today"} (${y.yearly}/year, less this month's credit). Renews yearly, cancel any time.`
    : "Charged to the card you pay for Basic with. Cancel any time.";

  return (
    <JamSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      tone="dark"
      eyebrow={offer === "yearly" ? "Switch to yearly" : "Upgrade to Pro"}
      title={
        offer === "yearly"
          ? "Same Basic, way less"
          : title || "Train your own voices"
      }
      className="md:tw-max-w-[460px]"
      footer={
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2.5">
          {error && (
            <p className="tw-m-0 tw-rounded-2xl tw-bg-white/10 tw-px-3 tw-py-2 tw-text-center tw-text-[13px] tw-font-bold tw-text-white">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={upgrade}
            disabled={busy || !preview}
            className="tw-flex tw-min-h-[52px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-5 tw-text-base tw-font-extrabold tw-text-jam-ink tw-transition hover:tw-brightness-95 active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
          >
            {busy && (
              <Spinner size={4} className="tw-border-3 tw-border-jam-ink" />
            )}
            {cta}
          </button>
          {preview && (
            <p className="tw-m-0 tw-text-center tw-text-xs tw-font-bold tw-leading-snug tw-text-jam-soft">
              {fine}
            </p>
          )}
          {coverMaking && (
            <p className="tw-m-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-xs tw-font-bold tw-text-white/80">
              <span className="tw-h-2 tw-w-2 tw-animate-pulse tw-rounded-full tw-bg-jam-lime" />
              Your cover keeps making while you decide
            </p>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={close}
            className="tw-cursor-pointer tw-self-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-[13px] tw-font-extrabold tw-text-white/70 hover:tw-text-white"
          >
            Not now
          </button>
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Perks
          items={
            offer === "yearly"
              ? [
                  "Same plan, nothing changes",
                  y && y.pct > 0
                    ? `Save ${y.pct}% on every month`
                    : "Your price locked for 12 months",
                  "This month's payment counts towards it",
                ]
              : [
                  "Train up to 25 voices a month",
                  "Marble Run and lyric videos in 1080p",
                  "50 video coins every month",
                  "Everything in Basic",
                ]
          }
        />
        <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-[20px] tw-bg-white/10 tw-px-4 tw-py-3.5">
          {!preview ? (
            isError ? (
              <span className="tw-text-sm tw-font-bold">
                We couldn&apos;t price this just now. You can change plans any
                time in Settings.
              </span>
            ) : (
              <span className="tw-flex tw-h-10 tw-items-center tw-justify-center">
                <Spinner size={4} className="tw-border-3 tw-border-white" />
              </span>
            )
          ) : y ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-py-1">
              {y.monthly && (
                <s className="tw-text-xl tw-font-extrabold tw-text-jam-soft">
                  {y.monthly}
                </s>
              )}
              <b className="jam-display tw-text-[46px] tw-leading-none tw-text-jam-lime">
                {y.perMonth}
              </b>
              <span className="tw-text-[15px] tw-font-extrabold tw-text-[#E3D3F7]">
                /month
              </span>
            </div>
          ) : (
            <>
              <div className="tw-flex tw-items-baseline tw-justify-between tw-text-sm tw-font-extrabold">
                Today
                <b className="jam-display tw-text-[22px]">
                  {creatorToday > 0 ? money(creatorToday) : "Nothing"}
                </b>
              </div>
              <small className="tw-text-[11.5px] tw-font-semibold tw-leading-snug tw-text-jam-soft">
                The rest of this month on Pro, minus what you&apos;ve already
                paid for Basic.
              </small>
              <div className="tw-flex tw-items-baseline tw-justify-between tw-text-[13px] tw-font-bold tw-text-[#E3D3F7]">
                Then
                <span>{money(preview.regularAmount)}/month</span>
              </div>
            </>
          )}
        </div>
      </div>
    </JamSheet>
  );
}
